import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Who the clinician currently has open.
 *
 * WHY THIS EXISTS. The Job Center drawer is mounted once in main.tsx, outside
 * <App>, so it can float above every route. That placement is also why it could
 * not answer "whose chart is this clinician looking at?" -- the selected patient
 * lives in App's own useState and never left it. The drawer therefore showed a
 * clinician every job they had ever started, across every patient, which is the
 * cross-patient list the clinician view is not supposed to have.
 *
 * TWO SOURCES, ROUTE WINS. A patient-scoped URL is unambiguous and survives a
 * refresh or a pasted link, so /patient/:patientId/* is read straight from the
 * location and takes precedence. Everything else -- /chat above all, where
 * selection is a click inside App and never reaches the URL -- is pushed in by
 * App calling setActivePatient. Route-first means a hard reload on a patient
 * page scopes the drawer correctly before App has mounted or fetched anything.
 *
 * NOT AN IDENTITY OR ACCESS BOUNDARY. This says which chart is on screen, and
 * nothing more. The server already scopes /v1/jobs by owner and admin role
 * (see _owner_scope in march.api.job_routes); this only narrows what is worth
 * showing a clinician who is mid-consult. Never treat it as authorization.
 */

export interface ActivePatient {
  patientId: string;
  patientName: string;
}

interface ActivePatientContextValue {
  /** The open patient, or null when the clinician is not in a chart. */
  active: ActivePatient | null;
  /** Called by App when in-app selection changes; ignored while a patient route is active. */
  setActivePatient: (patient: ActivePatient | null) => void;
}

const ActivePatientContext = createContext<ActivePatientContextValue | null>(null);

/** `/patient/:patientId/...` -> the id, else null. */
function patientIdFromPath(pathname: string): string | null {
  const match = /^\/patient\/([^/]+)(?:\/|$)/.exec(pathname);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]).trim() || null;
  } catch {
    // A malformed escape in the URL must not take the drawer down with it.
    return match[1].trim() || null;
  }
}

export function ActivePatientProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [selected, setSelected] = useState<ActivePatient | null>(null);

  const setActivePatient = useCallback((patient: ActivePatient | null) => {
    setSelected((prev) => {
      if (prev?.patientId === patient?.patientId && prev?.patientName === patient?.patientName) {
        return prev; // identical payload -- keep the reference so consumers don't re-render
      }
      return patient;
    });
  }, []);

  const routePatientId = patientIdFromPath(pathname);

  const active = useMemo<ActivePatient | null>(() => {
    if (routePatientId) {
      // Reuse the pushed name when it is for this same patient: the URL carries
      // an id, and a bare id is a poor thing to show a clinician.
      const name = selected?.patientId === routePatientId ? selected.patientName : '';
      return { patientId: routePatientId, patientName: name };
    }
    return selected;
  }, [routePatientId, selected]);

  const value = useMemo(() => ({ active, setActivePatient }), [active, setActivePatient]);

  return <ActivePatientContext.Provider value={value}>{children}</ActivePatientContext.Provider>;
}

/**
 * Safe outside the provider: returns "no patient open" rather than throwing, so
 * a component rendered in a test or a stray subtree cannot crash the app over
 * something this advisory.
 */
export function useActivePatient(): ActivePatientContextValue {
  return (
    useContext(ActivePatientContext) ?? {
      active: null,
      setActivePatient: () => {},
    }
  );
}
